// Help Center template — professional, on-theme knowledge base with coming-soon launch state
"use client";

import React, { useState, useMemo } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import Section from "@/components/public/Section";
import { DarkHeroHeader } from "@/components/public/DarkHeroHeader";
import { HomepageFAQs } from "@/components/public/homepage-FAQs";
import FeatureGrid from "@/components/public/FeatureGrid";
import {
  Search,
  ArrowRight,
  BookOpen,
  CheckCircle,
  Bell,
  Sparkles,
} from "lucide-react";

// ─── Data interfaces ──────────────────────────────────────────────────────────

export interface HelpCategory {
  icon: string;
  title: string;
  description: string;
  articleCount?: number;
}

export interface HelpFaq {
  question: string;
  answer: string;
}

export interface HelpQuickLink {
  href: string;
  label: string;
  description?: string;
}

export interface HelpCenterData {
  eyebrow: string;
  title: string;
  description: string;
  searchPlaceholder?: string;
  categories: HelpCategory[];
  faqs: HelpFaq[];
  quickLinks?: HelpQuickLink[];
  comingSoon?: {
    enabled: boolean;
    heading: string;
    description: string;
    features?: string[];
  };
}

// ─── HelpCenterTemplate ───────────────────────────────────────────────────────

interface HelpCenterTemplateProps {
  data: HelpCenterData;
}

export function HelpCenterTemplate({ data }: HelpCenterTemplateProps) {
  const {
    eyebrow,
    title,
    description,
    searchPlaceholder,
    categories,
    faqs,
    quickLinks,
    comingSoon,
  } = data;

  const [searchQuery, setSearchQuery] = useState("");

  // Filter FAQs by search query
  const filteredFaqs = useMemo(() => {
    if (!searchQuery.trim()) return faqs;
    const q = searchQuery.toLowerCase();
    return faqs.filter(
      (faq) =>
        faq.question.toLowerCase().includes(q) ||
        faq.answer.toLowerCase().includes(q)
    );
  }, [searchQuery, faqs]);

  // Map categories to FeatureGrid format
  const categoryFeatures = categories.map((cat) => ({
    icon: cat.icon,
    title: cat.title,
    description: cat.articleCount
      ? `${cat.description} · ${cat.articleCount} articles`
      : cat.description,
  }));

  return (
    <>
      {/* Dark hero header with search */}
      <DarkHeroHeader eyebrow={eyebrow} title={title} description={description}>
        {/* Search bar */}
        <div className="mt-8 max-w-xl">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={searchPlaceholder ?? "Search help articles, FAQs, guides..."}
              className="w-full pl-12 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent focus:bg-white/10 transition-all"
            />
          </div>
        </div>
      </DarkHeroHeader>

      {/* Coming Soon banner */}
      {comingSoon?.enabled && (
        <Section spacing="normal" bgColor="#faf5ff">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="rounded-2xl border border-purple-100 bg-white p-8 sm:p-10"
          >
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-10">
              {/* Icon + text */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
                    style={{ backgroundColor: "#a855f7" }}
                  >
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-satoshi font-bold text-gray-900">
                    {comingSoon.heading}
                  </h2>
                </div>
                <p className="text-sm sm:text-base font-light text-gray-600 leading-relaxed mb-4">
                  {comingSoon.description}
                </p>
                {comingSoon.features && comingSoon.features.length > 0 && (
                  <ul className="space-y-2">
                    {comingSoon.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-purple-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* CTA / notify */}
              <div className="shrink-0 w-full lg:w-auto">
                <Link
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 w-full lg:w-auto px-6 py-3 rounded-xl font-semibold text-white text-sm transition-opacity hover:opacity-90"
                  style={{ backgroundColor: "#a855f7" }}
                >
                  <Bell className="w-4 h-4" />
                  Contact us in the meantime
                </Link>
              </div>
            </div>
          </motion.div>
        </Section>
      )}

      {/* Help Categories — FeatureGrid */}
      {categoryFeatures.length > 0 && (
        <Section spacing="relaxed" background="transparent">
          <div className="mb-8 px-4 lg:px-6 relative">
            <div
              className="absolute left-0 top-0 w-0.5 h-10 rounded-full hidden lg:block"
              style={{ backgroundColor: "var(--mininav-active-color)" }}
            />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
            </motion.div>
          </div>
          <FeatureGrid
            features={categoryFeatures}
            cols={categoryFeatures.length >= 4 ? 4 : 3}
            showAccent={true}
          />
        </Section>
      )}

      {/* Quick Links panel */}
      {quickLinks && quickLinks.length > 0 && (
        <Section spacing="normal" bgColor="#f8fafc">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {quickLinks.map((link, i) => (
              <motion.div
                key={link.href}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                viewport={{ once: true }}
              >
                <Link
                  href={link.href}
                  className="group flex items-start gap-4 rounded-xl border border-gray-100 bg-white p-5 hover:border-purple-200 hover:shadow-sm transition-all"
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-white shrink-0"
                    style={{ backgroundColor: "var(--mininav-active-color, #a855f7)" }}
                  >
                    <BookOpen className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">
                      {link.label}
                    </p>
                    {link.description && (
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                        {link.description}
                      </p>
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 shrink-0 mt-0.5 group-hover:text-purple-400 group-hover:translate-x-0.5 transition-all" />
                </Link>
              </motion.div>
            ))}
          </div>
        </Section>
      )}

      {/* FAQs — filterable */}
      {filteredFaqs.length > 0 && (
        <>
          <div className="w-full border-t border-gray-200" />
          <Section spacing="relaxed" bgColor="#fafbfc">
            <HomepageFAQs
              title={searchQuery ? `Results for "${searchQuery}"` : "Frequently Asked Questions"}
              description={
                searchQuery
                  ? `${filteredFaqs.length} result${filteredFaqs.length !== 1 ? "s" : ""} found`
                  : "Quick answers to the most common questions about Replenico."
              }
              faqs={filteredFaqs}
            />
          </Section>
        </>
      )}

      {/* No results state */}
      {searchQuery && filteredFaqs.length === 0 && (
        <Section spacing="relaxed" background="transparent">
          <div className="text-center py-12">
            <Search className="w-10 h-10 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              No results for &ldquo;{searchQuery}&rdquo;
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Try a different search term or browse the categories above.
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
              style={{ color: "#a855f7" }}
            >
              Contact support instead
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </Section>
      )}

      {/* Bottom CTA */}
      <Section spacing="relaxed" bgColor="var(--homepage-primary, #0d2434)">
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-satoshi font-bold text-white mb-3">
              Can&apos;t find what you need?
            </h2>
            <p className="text-sm sm:text-base font-light text-white/60 leading-relaxed mb-8 max-w-lg mx-auto">
              Our operations team is available to help with anything not covered here.
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white text-sm transition-opacity hover:opacity-90"
              style={{ backgroundColor: "#a855f7" }}
            >
              Contact Support
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </Section>
    </>
  );
}

export default HelpCenterTemplate;
